/**
 * Pinned from `/Users/saher/code/cafe/Tarabeeza-Backend/src/modules/storefront/`
 * (read-only source — never edit that checkout from here):
 *   - storefront.controller.ts + storefront.service.ts
 *   - dto/storefront-settings.dto.ts, storefront-branch.dto.ts,
 *     storefront-domain.dto.ts, storefront-query.dto.ts, verify-domain.dto.ts
 *   - prisma/schema.prisma (Storefront, StorefrontBranch, StorefrontDomain,
 *     StorefrontVisit, and their enums)
 * One file, so a later drift on that side is a one-file fix here.
 *
 * ADR-0016 in the sentences that shape this file: a domain is a CLAIM the
 * moment it is added, and a BOUNDARY only once `verifiedAt` is set — an
 * unverified row exists and resolves nothing, which is what stops a tenant
 * serving its own site under a name it does not control. The backend never
 * performs the DNS lookup itself (an outbound request an untrusted hostname
 * would steer); the operator publishes the TXT record this file's
 * `verificationRecord` names, and pastes back what they observed, which is
 * what `DomainsTab` asks for instead of a "verify" button that magically
 * works.
 *
 * `storefront.*` (`view`/`update`/`publish`/`manage_domains`) is a real
 * permission resource but is not assigned to any SEEDED role except Tenant
 * Owner/Admin (who get every non-platform key via `ALL_PERMISSION_KEYS`) —
 * see `roles.ts`. The controller's own comment imagines a marketing role
 * that holds `update` but not `publish`; nothing seeded exercises that split
 * yet, which is worth knowing before promising a marketing user this screen.
 */
import { apiError, apiStatus } from '../Workforce/lib';

export { apiError, apiStatus };

export const StorefrontStatus = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
  SUSPENDED: 'SUSPENDED',
} as const;
export type StorefrontStatus = (typeof StorefrontStatus)[keyof typeof StorefrontStatus];

export const StorefrontPaymentPolicy = {
  PAY_ON_PICKUP: 'PAY_ON_PICKUP',
  ONLINE: 'ONLINE',
  BOTH: 'BOTH',
} as const;
export type StorefrontPaymentPolicy =
  (typeof StorefrontPaymentPolicy)[keyof typeof StorefrontPaymentPolicy];

export const StorefrontDomainKind = { SUBDOMAIN: 'SUBDOMAIN', CUSTOM: 'CUSTOM' } as const;
export type StorefrontDomainKind = (typeof StorefrontDomainKind)[keyof typeof StorefrontDomainKind];

export const StorefrontFulfillmentMode = {
  PICKUP_ASAP: 'PICKUP_ASAP',
  PICKUP_SCHEDULED: 'PICKUP_SCHEDULED',
  DELIVERY: 'DELIVERY',
} as const;
export type StorefrontFulfillmentMode =
  (typeof StorefrontFulfillmentMode)[keyof typeof StorefrontFulfillmentMode];

// ------------------------------------------------------------------ SEO

export interface StorefrontSeoLocale {
  title?: string;
  description?: string;
  imageUrl?: string;
}

export interface StorefrontSeo {
  byLocale?: Record<string, StorefrontSeoLocale>;
  /** Off serves `noindex` — a pilot, or a domain that is not live yet. */
  indexable: boolean;
}

export interface StorefrontAnalyticsProvider {
  provider: string;
  id: string;
}

export interface StorefrontAnalytics {
  requireConsent: boolean;
  providers: StorefrontAnalyticsProvider[];
}

// ------------------------------------------------------------- storefront

/** `StorefrontController.view()` — `seo`/`analytics` always come back parsed, never a raw blob. */
export interface Storefront {
  id: string;
  name: string;
  status: StorefrontStatus;
  defaultLocale: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  themeColor: string | null;
  description: string | null;
  seo: StorefrontSeo;
  analytics: StorefrontAnalytics;
  paymentPolicy: StorefrontPaymentPolicy;
  /** Must stay `true` — turning it off means "require an account", and there is no customer identity yet (ADR-0016 §2.7). */
  guestCheckout: boolean;
  scheduleHorizonDays: number;
  abandonAfterMinutes: number;
}

export interface UpsertStorefrontInput {
  name?: string;
  /** Present only when the actor also holds `storefront.publish` — see `assertMayPublish`. */
  status?: StorefrontStatus;
  defaultLocale?: string;
  logoUrl?: string;
  faviconUrl?: string;
  themeColor?: string;
  description?: string;
  seo?: StorefrontSeo;
  analytics?: StorefrontAnalytics;
  paymentPolicy?: StorefrontPaymentPolicy;
  scheduleHorizonDays?: number;
  abandonAfterMinutes?: number;
}

// ---------------------------------------------------------------- branches

/** `MenuScheduleWindowDto` — the SAME shape `ScheduleEditor` already edits for a menu's own hours. */
export interface ScheduleWindow {
  days: number[];
  from: string;
  to: string;
}

export interface StorefrontBranch {
  id: string;
  branchId: string;
  isPublished: boolean;
  /** The panic switch — published and "open" on paper, still refuses orders while this is off. */
  acceptingOrders: boolean;
  pickupEnabled: boolean;
  /** ADR-0022 owns own-delivery; this stays `false` and the backend refuses `true` (§16 not built). */
  deliveryEnabled: boolean;
  pickupLeadMinutes: number;
  /** Empty/null = open all day. */
  serviceHours: ScheduleWindow[] | null;
  branch: { id: string; name: string; timezone: string; isActive: boolean };
}

export interface UpsertStorefrontBranchInput {
  branchId: string;
  isPublished?: boolean;
  acceptingOrders?: boolean;
  pickupEnabled?: boolean;
  deliveryEnabled?: boolean;
  pickupLeadMinutes?: number;
  serviceHours?: ScheduleWindow[];
}

// ----------------------------------------------------------------- domains

/** `StorefrontService.domainView()`. */
export interface StorefrontDomain {
  id: string;
  hostname: string;
  kind: StorefrontDomainKind;
  isPrimary: boolean;
  /** The gate. Null = a claim, not yet a boundary — see this file's header note. */
  verifiedAt: string | null;
  verificationRecord: { name: string; type: 'TXT'; value: string };
}

export interface AddStorefrontDomainInput {
  hostname: string;
  isPrimary?: boolean;
}

// -------------------------------------------------------------- abandoned

/** No contact fields, deliberately — a metric, never a re-marketing list (ADR-0016 §2.8). */
export interface AbandonedCart {
  id: string;
  branchId: string;
  fulfillmentMode: StorefrontFulfillmentMode;
  cartItemCount: number;
  /** Decimal string, no currency on the record — see `CartTotal` below. */
  cartTotal: string | null;
  cartUpdatedAt: string | null;
  abandonedAt: string;
  createdAt: string;
}
